import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/stand-counts';

// NOT: /api/stand-counts OData değil — özel REST endpoint (ApiResponse<{items,counts}>),
// max 500 kayıt döndürür. Bu yüzden useC360List (OData $filter/$top/$skip) kullanılamaz.
// Sunucu condition + tarih ile filtreler; serbest metin / kolon filtresi / sıralama /
// sayfalama c360 görünümü için istemci tarafında uygulanır.

const CONDITION_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'OK', label: 'Sağlam' },
  { key: 'FaultRequested', label: 'Arıza Bildirildi' },
  { key: 'Replaced', label: 'Değiştirildi' },
];

const CONDITION_META = {
  OK: { label: 'Sağlam', tone: 'ok' },
  FaultRequested: { label: 'Arıza Bildirildi', tone: 'bad' },
  Replaced: { label: 'Değiştirildi', tone: 'wait' },
};

// StandCountItem.PlacementType — Shelf/Door/Top/Other.
const PLACEMENT_LABELS = {
  Shelf: 'Raf',
  Door: 'Kapak',
  Top: 'Üst',
  Other: 'Diğer',
};

// İstemci tarafı kolon filtreleri (REST endpoint OData desteklemediği için clientSide).
const COLUMN_FILTER_META = {
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  standCode: { type: 'text', odataField: 'StandCode' },
  condition: {
    type: 'select', odataField: 'Condition',
    options: [
      { value: 'OK', label: 'Sağlam' },
      { value: 'FaultRequested', label: 'Arıza Bildirildi' },
      { value: 'Replaced', label: 'Değiştirildi' },
    ],
  },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    okCount: pick(data, 'okCount', 'OkCount') ?? 0,
    faultCount: pick(data, 'faultCount', 'FaultCount') ?? 0,
    replacedCount: pick(data, 'replacedCount', 'ReplacedCount') ?? 0,
  };
}

function placementLabel(p) {
  if (!p) return '-';
  return PLACEMENT_LABELS[p] || p;
}

function conditionBadge(condition) {
  const meta = CONDITION_META[condition] || { label: condition || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

// Soğutucu ideal aralık 2-8°C — dışına çıkınca uyarı tonu.
function tempBadge(value) {
  if (value === undefined || value === null || value === '') return <span style={{ color: '#94a3b8' }}>-</span>;
  const t = Number(value);
  if (!Number.isFinite(t)) return <span style={{ color: '#94a3b8' }}>-</span>;
  let tone = 'ok';
  if (t < 2 || t > 8) tone = 'bad';
  else if (t < 3 || t > 7) tone = 'wait';
  return <StatusBadge tone={tone}>{`${t.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}°C`}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(dateStr) {
  if (!dateStr) return '';
  try { return new Date(`${dateStr}T00:00:00`).toISOString(); } catch { return ''; }
}

function toIsoEnd(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  } catch { return ''; }
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
  };
  const t = tones[tone] || tones.green;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function StandCountsPage() {
  usePageMeta('Soğutucu Sayımları', 'Müşteri soğutucu/stand içerik sayımları — sıcaklık, durum ve ürün kalemleri.');

  const [conditionFilter, setConditionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ okCount: 0, faultCount: 0, replacedCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CountedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [detailItems, setDetailItems] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (conditionFilter) params.set('condition', conditionFilter);
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ okCount: data.okCount, faultCount: data.faultCount, replacedCount: data.replacedCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [conditionFilter, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailItems(null);
    setDetailLoading(true);
    try {
      const id = pick(row, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${id}/items`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kalemler alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailItems(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Kalemler alınamadı');
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const applyFilterText = (text) => { setFilterText(text); setPage(1); };
  const setColumnFilter = (columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setPage(1);
  };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setPage(1);
  };

  const applySort = (odataField, dir) => {
    setSortField(odataField || 'CountedAt');
    setSortDir(dir || 'asc');
  };

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const textMatch = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'standCode', 'StandCode'),
        pick(row, 'standType', 'StandType'),
        pick(row, 'notes', 'Notes'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const colMatch = (row) => {
      for (const [columnId, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[columnId];
        if (!meta) continue;
        if (meta.type === 'select') {
          const v = filter.value;
          if (v === '' || v == null) continue;
          if (String(pick(row, columnId, columnId.charAt(0).toUpperCase() + columnId.slice(1)) ?? '') !== String(v)) return false;
        } else if (meta.type === 'text') {
          const v = (filter.value ?? '').toString().toLocaleLowerCase('tr-TR');
          if (!v) continue;
          const cell = String(pick(row, columnId, columnId.charAt(0).toUpperCase() + columnId.slice(1)) ?? '').toLocaleLowerCase('tr-TR');
          if (!cell.includes(v)) return false;
        }
      }
      return true;
    };
    const filtered = rows.filter((r) => textMatch(r) && colMatch(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'StandCode': return pick(row, 'standCode', 'StandCode') || '';
        case 'Condition': return pick(row, 'condition', 'Condition') || '';
        case 'TemperatureCelsius': return Number(pick(row, 'temperatureCelsius', 'TemperatureCelsius')) || 0;
        case 'TotalQuantity': return Number(pick(row, 'totalQuantity', 'TotalQuantity')) || 0;
        case 'ItemCount': return Number(pick(row, 'itemCount', 'ItemCount')) || 0;
        case 'CountedAt':
        default: {
          const v = pick(row, 'countedAt', 'CountedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('countedAt', {
      header: 'Sayım Tarihi', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('standCode', {
      header: 'Soğutucu', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const code = pick(row.original, 'standCode', 'StandCode');
        const type = pick(row.original, 'standType', 'StandType');
        return (
          <div>
            {code ? <span className="c360-erp-chip">{code}</span> : '-'}
            {type && <div style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>{type}</div>}
          </div>
        );
      },
    }),
    columnHelper.accessor('temperatureCelsius', {
      header: 'Sıcaklık', enableColumnFilter: false,
      cell: (info) => tempBadge(info.getValue()),
    }),
    columnHelper.accessor('condition', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => conditionBadge(info.getValue()),
    }),
    columnHelper.accessor('totalQuantity', {
      header: 'Toplam Adet', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor('itemCount', {
      header: 'Kalem', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('CountedAt', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: page - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    try {
      const cols = [
        { headerName: 'Sayım Tarihi', valueGetter: ({ row }) => formatDate(pick(row, 'countedAt', 'CountedAt')) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Soğutucu Kodu', valueGetter: ({ row }) => pick(row, 'standCode', 'StandCode') || '' },
        { headerName: 'Soğutucu Türü', valueGetter: ({ row }) => pick(row, 'standType', 'StandType') || '' },
        { headerName: 'Sıcaklık (°C)', valueGetter: ({ row }) => pick(row, 'temperatureCelsius', 'TemperatureCelsius') ?? '' },
        { headerName: 'Durum', valueGetter: ({ row }) => (CONDITION_META[pick(row, 'condition', 'Condition')]?.label || pick(row, 'condition', 'Condition') || '') },
        { headerName: 'Toplam Adet', valueGetter: ({ row }) => pick(row, 'totalQuantity', 'TotalQuantity') ?? 0 },
        { headerName: 'Kalem', valueGetter: ({ row }) => pick(row, 'itemCount', 'ItemCount') ?? 0 },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `sogutucu-sayim_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {CONDITION_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setConditionFilter(tab.key)}
          style={{
            ...(conditionFilter === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Başlangıç"
      />
      <input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Bitiş"
      />
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri / soğutucu kodu..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={page}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Soğutucu Sayımları"
        subtitle="Saha temsilcisinin müşteri soğutucu/standlarında yaptığı içerik sayımları — sıcaklık, durum ve ürün kalemleri."
        meta={(
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {badge('Sağlam', counts.okCount, 'green')}
            {badge('Arıza', counts.faultCount, 'red')}
            {badge('Değiştirildi', counts.replacedCount, 'orange')}
          </div>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => { setDetailRow(null); setDetailItems(null); }}>
          <div style={{ ...s.modal, maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Soğutucu Sayımı'}
                {pick(detailRow, 'standCode', 'StandCode') ? ` • ${pick(detailRow, 'standCode', 'StandCode')}` : ''}
              </h3>
              <button
                type="button"
                style={s.closeBtn}
                onClick={() => { setDetailRow(null); setDetailItems(null); }}
              >×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{conditionBadge(pick(detailRow, 'condition', 'Condition'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sıcaklık</label>
                  <div>{tempBadge(pick(detailRow, 'temperatureCelsius', 'TemperatureCelsius'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Soğutucu Türü</label>
                  <div style={s.inputView}>{pick(detailRow, 'standType', 'StandType') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sayım Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'countedAt', 'CountedAt'))}</div>
                </div>
              </div>
              {pick(detailRow, 'photoUrl', 'PhotoUrl') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Fotoğraf</label>
                  <a href={pick(detailRow, 'photoUrl', 'PhotoUrl')} target="_blank" rel="noreferrer"
                     style={{ display: 'block', width: 140, height: 140, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <img src={pick(detailRow, 'photoUrl', 'PhotoUrl')} alt="Soğutucu"
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                  </a>
                </div>
              )}
              {pick(detailRow, 'notes', 'Notes') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{pick(detailRow, 'notes', 'Notes')}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Ürün Kalemleri ({detailItems?.length ?? '...'})</label>
                {detailLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}
                {!detailLoading && detailItems && (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Yerleşim</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailItems.length === 0 && (
                          <tr><td colSpan="3" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Kalem yok</td></tr>
                        )}
                        {detailItems.map((item, idx) => (
                          <tr key={pick(item, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                              {pick(item, 'productName', 'ProductName') || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                              {placementLabel(pick(item, 'placementType', 'PlacementType'))}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(item, 'quantity', 'Quantity') ?? '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.btnSecondary}
                onClick={() => { setDetailRow(null); setDetailItems(null); }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default StandCountsPage;
